import { type TableColumn, safeAbsurd } from '@ipfabric/utils';
import type { RenderedTableCell } from 'components/Table/cells/RenderedTableCell';
import { renderHostnameCell } from 'components/Table/cells/renderHostnameCell';
import { renderNumberCell } from 'components/Table/cells/renderNumberCell';
import { renderTextCell } from 'components/Table/cells/renderTextCell';
import { namedCellContentRenderFunctions } from 'components/Table/customRender/namedCellContentRenderFunctions';
import type {
  RenderCellContentArguments,
  TableCellContentRenderFunction,
} from 'components/Table/model/TableCellContentRenderFunction';
import { renderAsnCell } from './renderAsnCell';
import { renderBgpRoutingCell } from './renderBgpRoutingCell';
import { renderBooleanCell } from './renderBooleanCell';
import { renderCompactNumberCell } from './renderCompactNumberCell';
import { renderDateTimeCell } from './renderDateTimeCell';
import { renderEnumCell } from './renderEnumCell';
import { renderFrontendOnlyCell } from './renderFrontendOnlyCell';
import { renderHostnameObjectCell } from './renderHostnameObjectCell';
import { renderInterfaceCell } from './renderInterfaceCell';
import { renderLabelsCell } from './renderLabelsCell';
import { renderMplsNextHopCell } from './renderMplsNextHopCell';
import { renderNeighborIpCell } from './renderNeighborIpCell';
import { renderNtpSummarySourcesCell } from './renderNtpSummarySources';
import { renderObjectCell } from './renderObjectCell';
import { renderSecurityCell } from './renderSecurityCell';
import { renderSwitchportIntNameCell } from './renderSwitchportIntNameCell';
import { renderUptimeCell } from './renderUptimeCell';
import { renderUrlCell } from './renderUrlCell';

export const renderCellContentByType = (
  column: TableColumn<any>,
  args: RenderCellContentArguments,
): RenderedTableCell => {
  if (column.renderContent) {
    return (column.renderContent as TableCellContentRenderFunction)(args);
  }
  if (column.renderContentNamed) {
    return namedCellContentRenderFunctions[column.renderContentNamed](args);
  }
  const { format } = column as TableColumn<unknown>;
  switch (format.type) {
    case 'hostname':
      return renderHostnameCell(format, args);
    case 'text':
      return renderTextCell(format, args);
    case 'number':
      return renderNumberCell(format, args);
    case 'boolean':
      return renderBooleanCell(format, args);
    case 'uptime':
      return renderUptimeCell(format, args);
    case 'object':
      return renderObjectCell(format, args);
    case 'dateTime':
      return renderDateTimeCell(format, args);
    case 'compactNumber':
      return renderCompactNumberCell(format, args);
    case 'enum':
      return renderEnumCell(format, args);
    case 'url':
      return renderUrlCell(format, args);
    case 'security':
      return renderSecurityCell(format, args);
    case 'mplsNextHop':
      return renderMplsNextHopCell(format, args);
    case 'bgpRouting':
      return renderBgpRoutingCell(format, args);
    case 'ntpSummarySources':
      return renderNtpSummarySourcesCell(format, args);
    case 'switchportIntName':
      return renderSwitchportIntNameCell(format, args);
    case 'neighborIp':
      return renderNeighborIpCell(format, args);
    case 'hostnameObject':
      return renderHostnameObjectCell(format, args);
    case 'interface':
      return renderInterfaceCell(format, args);
    case 'labels':
      return renderLabelsCell(format, args);
    case 'asn':
      return renderAsnCell(format, args);
    case 'frontendOnly':
      return renderFrontendOnlyCell(format, args);
    default:
      safeAbsurd(format, `unknown column format "${(format as any).type}"`);
      return {
        content: null,
      };
  }
};
